import { Head, router } from "@inertiajs/react";
import { AlertCircle, CheckCircle, CircleAlert, Loader2 } from "lucide-react";
import { useState } from "react";

import { OnboardingLayout } from "@/components/onboarding/onboarding-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type StepKey = "connect_domain" | "sign_up" | "branding" | "api_key";

interface OnboardingStepState {
  key: StepKey;
  status: "pending" | "completed" | "skipped";
  completedAt?: string;
}

interface DomainStepProps {
  domain: DomainInfo | null;
  claim: DomainClaim | null;
  onboarding: {
    currentStep: StepKey;
    steps: OnboardingStepState[];
  };
}

interface DomainInfo {
  id: number;
  hostname: string;
  verificationStatus: "unverified" | "verified" | "pending" | "failed";
  verificationRecordName: string;
  verificationRecordValue: string;
  verificationHostname: string;
  verificationCheckedAt?: string | null;
  verifiedAt?: string | null;
}

interface DomainClaim {
  id: number;
  status: "pending" | "verified" | "failed" | "expired" | "canceled";
  hostname: string;
  recordName: string;
  recordValue: string;
  expiresAt?: string | null;
  checkedAt?: string | null;
  verifiedAt?: string | null;
}

interface DomainExample {
  example: string;
  description: string;
}

const DOMAIN_EXAMPLES: DomainExample[] = [
  {
    example: "example.com",
    description: "Your main domain",
  },
  {
    example: "blog.example.com",
    description: "A specific subdomain",
  },
  {
    example: "*.example.com",
    description: "Wildcard for all subdomains and main domain",
  },
];

export default function OnboardingDomainPage({ onboarding, domain: domainProps, claim }: DomainStepProps) {
  const [domain, setDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!domain.trim()) {
      setErrorMessage("Domain is required");
      setSubmitStatus("error");
      return;
    }

    const trimmedDomain = domain.trim();

    router.post(
      "/domains",
      {
        hostname: trimmedDomain,
        from_onboarding: true,
      },
      {
        forceFormData: true,
        preserveState: true,
        onStart: () => {
          setIsSubmitting(true);
          setSubmitStatus("idle");
          setErrorMessage("");
        },
        onSuccess: (page) => {
          const flash = (page.props as { flash?: { notice?: string; alert?: string } }).flash;

          if (flash?.notice) {
            setDomain("");
            setErrorMessage("");
            setSubmitStatus("success");
            // Redirect happens via controller
            return;
          }

          if (flash?.alert) {
            setErrorMessage(flash.alert);
            setSubmitStatus("error");
          }
        },
        onError: (errors) => {
          const errorMsg = errors.hostname || "Failed to register domain";
          setErrorMessage(errorMsg);
          setSubmitStatus("error");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  const getStatusIcon = () => {
    if (submitStatus === "success") {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (submitStatus === "error") {
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    }
    return null;
  };

  const getStatusMessage = () => {
    if (submitStatus === "success") {
      return "Domain registered successfully! Redirecting...";
    } else if (submitStatus === "error") {
      return errorMessage;
    }
    return "";
  };

  const hasDomain = Boolean(domainProps?.id);
  const isVerified = domainProps?.verificationStatus === "verified";
  const isVerificationFailed = domainProps?.verificationStatus === "failed";

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  return (
    <>
      <Head title="Connect Domain - Onboarding" />

      <OnboardingLayout
        title="Connect your domain"
        description="Add your domain and verify ownership with a DNS TXT record before generating OG images."
        onboarding={onboarding}
        onSkipStep={() => router.post("/onboarding/steps/connect_domain/skip")}
      >
        <div className="space-y-6">
          {hasDomain ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span>Verify {domainProps?.hostname}</span>
                  <Badge variant={isVerified ? "default" : "outline"}>{isVerified ? "Verified" : "Unverified"}</Badge>
                </CardTitle>
                <CardDescription>Add the TXT record below to prove domain ownership.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isVerified ? (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertTitle>Domain verified</AlertTitle>
                    <AlertDescription>
                      You&apos;re ready to continue to branding and start generating OG images.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive" className="border-amber-200 bg-amber-50 text-amber-900">
                    <CircleAlert className="h-4 w-4" />
                    <AlertTitle>Verification required</AlertTitle>
                    <AlertDescription>
                      OG image generation stays disabled until the DNS TXT record is verified.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-3 rounded-lg border p-4 text-sm">
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Record name</span>
                    <code className="bg-muted rounded px-2 py-1 font-mono">{domainProps?.verificationRecordName}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Record value</span>
                    <code className="bg-muted rounded px-2 py-1 font-mono">{domainProps?.verificationRecordValue}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Applies to</span>
                    <code className="bg-muted rounded px-2 py-1 font-mono">{domainProps?.verificationHostname}</code>
                  </div>
                </div>

                {domainProps?.verificationCheckedAt && (
                  <p className="text-muted-foreground text-xs">
                    Last checked: {formatDate(domainProps.verificationCheckedAt)}
                  </p>
                )}

                <div className="flex flex-wrap gap-3">
                  {!isVerified && (
                    <Button
                      onClick={() => router.post(`/domains/${domainProps?.id}/verify`, { from_onboarding: true })}
                      disabled={!domainProps?.id}
                    >
                      {isVerificationFailed ? "Try verification again" : "Verify domain"}
                    </Button>
                  )}
                  {isVerified && (
                    <Button onClick={() => router.visit("/onboarding/branding")}>Continue to Branding</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="domain">Domain Name</Label>
                <Input
                  id="domain"
                  type="text"
                  placeholder="example.com or *.example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={isSubmitting}
                  className={submitStatus === "error" ? "border-red-500" : ""}
                />

                {(submitStatus === "error" || submitStatus === "success") && (
                  <div className="lh-1 flex items-start gap-2 text-sm">
                    {getStatusIcon()}
                    <span className={submitStatus === "error" ? "text-red-600" : "text-green-600"}>
                      {getStatusMessage()}
                    </span>
                  </div>
                )}

                <p className="text-muted-foreground text-xs">Examples:</p>
                <div className="space-y-2">
                  {DOMAIN_EXAMPLES.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <code className="bg-muted rounded px-2 py-1 font-mono">{item.example}</code>
                      <span className="text-muted-foreground">{item.description}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting || !domain.trim()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registering...
                  </>
                ) : (
                  "Register Domain"
                )}
              </Button>
            </form>
          )}

          {claim?.status === "pending" && (
            <Card>
              <CardHeader>
                <CardTitle>Claim {claim.hostname}</CardTitle>
                <CardDescription>
                  This domain is currently unverified by another account. Add the TXT record below to claim ownership.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-3 rounded-lg border p-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Record name</span>
                    <code className="bg-muted rounded px-2 py-1 font-mono">{claim.recordName}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Record value</span>
                    <code className="bg-muted rounded px-2 py-1 font-mono">{claim.recordValue}</code>
                  </div>
                  {claim.expiresAt && (
                    <span className="text-muted-foreground text-xs">Claim expires: {formatDate(claim.expiresAt)}</span>
                  )}
                </div>

                <Button onClick={() => router.post(`/domain_claims/${claim.id}/verify`, { from_onboarding: true })}>
                  Verify claim
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </OnboardingLayout>
    </>
  );
}
